import { some } from 'lodash';

import { EnumUserRelationshipsType, EnumUserRole, User } from './graphql';

/**
 * Processor roles allowed to *view* a processor page, read-only accounts
 * included. Use this for any page whose write affordances are gated by
 * `canWrite`. Pages that exist only to perform a write (invoice generation,
 * adding a head) and Reporting stay on narrower lists that omit
 * `VendorReadonly`.
 */
export const PROCESSOR_VIEW_ROLES = [
  EnumUserRole.Vendor,
  EnumUserRole.VendorOwner,
  EnumUserRole.VendorReadonly,
  EnumUserRole.Admin,
];

/**
 * Processor roles allowed to perform a write action — `PROCESSOR_VIEW_ROLES`
 * minus `VendorReadonly`. Use this (not the view list) for nav items and routes
 * that exist to create/modify data, e.g. the "Add Job" quick-schedule action,
 * so read-only accounts don't get a write affordance. Mirrors {@link canWrite}.
 */
export const PROCESSOR_WRITE_ROLES = [
  EnumUserRole.Vendor,
  EnumUserRole.VendorOwner,
  EnumUserRole.Admin,
];

export function isAdmin(user: User) {
  return user?.role === EnumUserRole.Admin;
}

export function isProcessor(user: User) {
  return (
    user?.role === EnumUserRole.Admin ||
    user?.role === EnumUserRole.VendorOwner ||
    user?.role === EnumUserRole.Vendor ||
    user?.role === EnumUserRole.VendorReadonly
  );
}

export function isProducer(user: User) {
  return user?.role === EnumUserRole.Basic;
}

export function isVendorOwner(user: User) {
  return (
    user?.role === EnumUserRole.Admin || user?.role === EnumUserRole.VendorOwner
  );
}

/** A processor account with read-only access (blocked from all mutations). */
export function isReadOnlyProcessor(user: User) {
  return user?.role === EnumUserRole.VendorReadonly;
}

/**
 * Whether the user may perform write actions. Mirrors the server-side `can()`
 * policy: read-only processor accounts cannot write; everyone else can. This
 * only gates UI affordances — the API is the real enforcement point.
 */
export function canWrite(user: User) {
  return !!user && user.role !== EnumUserRole.VendorReadonly;
}

export function isBuyer(user: User) {
  return (
    user?.role === EnumUserRole.Basic &&
    some(
      user?.relationships,
      (r) => r?.type === EnumUserRelationshipsType.Buyer,
    )
  );
}

export function isBuyerOnly(user: User) {
  return (
    isBuyer(user) &&
    !some(
      user?.relationships,
      (r) => r?.type === EnumUserRelationshipsType.Producer,
    )
  );
}
